"use client";
import { useState, useEffect, useRef } from "react";
import { ArrowRight, Link as LinkIcon, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface TimelineItem {
  id: number;
  title: string;
  date: string;
  content: string;
  category: string;
  icon: React.ElementType;
  relatedIds: number[];
  status: "completed" | "in-progress" | "pending";
  energy: number;
}

interface RadialOrbitalTimelineProps {
  timelineData: TimelineItem[];
  centerLogo?: string;
  centerLabel?: string;
}

export default function RadialOrbitalTimeline({
  timelineData,
  centerLogo,
  centerLabel = "AmeUp",
}: RadialOrbitalTimelineProps) {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [pulseEffect, setPulseEffect] = useState<Record<number, boolean>>({});
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleContainerClick = (e: React.MouseEvent) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const getRelatedItems = (itemId: number): number[] => {
    const currentItem = timelineData.find((item) => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  };

  const centerViewOnNode = (nodeId: number) => {
    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const totalNodes = timelineData.length;
    const targetAngle = (nodeIndex / totalNodes) * 360;
    setRotationAngle(270 - targetAngle);
  };

  const toggleItem = (id: number) => {
    setExpandedItems((prev) => {
      const newState: Record<number, boolean> = {};
      Object.keys(prev).forEach((key) => {
        newState[parseInt(key)] = false;
      });
      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);
        const relatedItems = getRelatedItems(id);
        const newPulse: Record<number, boolean> = {};
        relatedItems.forEach((relId) => {
          newPulse[relId] = true;
        });
        setPulseEffect(newPulse);
        centerViewOnNode(id);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }
      return newState;
    });
  };

  useEffect(() => {
    if (!autoRotate) return;
    let rafId: number;
    let lastTs = performance.now();
    const DEG_PER_MS = 0.15 / 50; // preserva velocidade original (0.15° a cada 50ms)
    const tick = (ts: number) => {
      const delta = ts - lastTs;
      lastTs = ts;
      setRotationAngle((prev) => (prev + DEG_PER_MS * delta) % 360);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [autoRotate]);

  const calculateNodePosition = (index: number, total: number) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = isMobile ? 85 : 175;
    const radian = (angle * Math.PI) / 180;
    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(0.5, Math.min(1, 0.5 + 0.5 * ((1 + Math.sin(radian)) / 2)));
    return { x, y, zIndex, opacity };
  };

  const isRelatedToActive = (itemId: number): boolean => {
    if (!activeNodeId) return false;
    return getRelatedItems(activeNodeId).includes(itemId);
  };

  const getStatusStyles = (status: TimelineItem["status"]): string => {
    switch (status) {
      case "completed":
        return "text-[#EEFDA3] bg-foreground border-foreground";
      case "in-progress":
        return "text-foreground bg-[#EEFDA3] border-foreground";
      case "pending":
        return "text-foreground bg-background border-foreground/50";
      default:
        return "text-foreground bg-background border-foreground/50";
    }
  };

  return (
    <div
      className="w-full h-[450px] sm:h-[560px] flex items-center justify-center overflow-hidden"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className="relative w-full max-w-2xl h-full flex items-center justify-center" ref={orbitRef}>
        <div className="absolute w-full h-full flex items-center justify-center pointer-events-none">
          {/* Center logo */}
          <div className="absolute w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-foreground flex items-center justify-center shadow-2xl z-10 pointer-events-auto p-3 sm:p-6">
            {centerLogo ? (
              <img src={centerLogo} alt={centerLabel} className="w-full h-full object-contain" />
            ) : (
              <span className="text-[#EEFDA3] text-sm font-bold">{centerLabel}</span>
            )}
          </div>

          {/* Orbit ring */}
          <div className={`absolute rounded-full border-2 border-foreground/15 ${isMobile ? "w-[170px] h-[170px]" : "w-[350px] h-[350px]"}`} />

          {timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length);
            const isExpanded = expandedItems[item.id];
            const isRelated = isRelatedToActive(item.id);
            const isPulsing = pulseEffect[item.id];
            const Icon = item.icon;

            const nodeStyle = {
              transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
              zIndex: isExpanded ? 200 : position.zIndex,
              opacity: isExpanded ? 1 : position.opacity,
              transition: "opacity 300ms ease-out",
              willChange: "transform",
            };

            return (
              <div
                key={item.id}
                ref={(el) => (nodeRefs.current[item.id] = el)}
                className="absolute cursor-pointer pointer-events-auto"
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >
                {isPulsing && (
                  <div className="absolute inset-0 -m-2 rounded-full animate-ping bg-foreground/20" />
                )}

                <div
                  className={`
                    w-12 h-12 sm:w-20 sm:h-20 rounded-full flex items-center justify-center
                    bg-background border-2 shadow-lg transition-all duration-300
                    ${isExpanded ? "border-foreground scale-110" : isRelated ? "border-foreground/70" : "border-foreground/30"}
                  `}
                >
                  <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-foreground" />
                </div>

                <div
                  className={`
                    absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap
                    text-[10px] sm:text-sm font-bold text-foreground transition-all duration-300
                    ${isExpanded ? "scale-110" : ""}
                  `}
                >
                  {item.title}
                </div>

                {isExpanded && (
                  <Card
                    className="absolute top-16 sm:top-28 left-1/2 -translate-x-1/2 w-[200px] bg-background border-2 border-foreground shadow-2xl overflow-visible"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-foreground/50" />
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <Badge className={`px-2 text-[10px] ${getStatusStyles(item.status)}`}>
                          {item.status === "completed"
                            ? "COMPLETO"
                            : item.status === "in-progress"
                              ? "ATIVO"
                              : "EM BREVE"}
                        </Badge>
                        <span className="text-xs text-foreground/60">{item.date}</span>
                      </div>
                      <CardTitle className="text-sm mt-2 text-foreground">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-foreground/80">
                      <p>{item.content}</p>

                      <div className="mt-4 pt-3 border-t border-foreground/10">
                        <div className="flex justify-between items-center text-[10px] mb-1">
                          <span className="flex items-center text-foreground/70">
                            <Zap className="w-3 h-3 mr-1" /> Impacto
                          </span>
                          <span className="font-mono text-foreground">{item.energy}%</span>
                        </div>
                        <div className="w-full h-1 bg-foreground/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-foreground"
                            style={{ width: `${item.energy}%` }}
                          />
                        </div>
                      </div>

                      {item.relatedIds.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-foreground/10">
                          <div className="flex items-center mb-2">
                            <LinkIcon className="w-3 h-3 text-foreground/70 mr-1" />
                            <h4 className="text-[10px] uppercase tracking-wider font-medium text-foreground/70">
                              Conectado a
                            </h4>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {item.relatedIds.map((relatedId) => {
                              const relatedItem = timelineData.find((i) => i.id === relatedId);
                              return (
                                <Badge
                                  key={relatedId}
                                  variant="outline"
                                  className="flex items-center h-6 px-2 py-0 text-[10px] border-foreground/40 text-foreground hover:bg-foreground hover:text-background transition-colors cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItem(relatedId);
                                  }}
                                >
                                  {relatedItem?.title}
                                  <ArrowRight className="w-2.5 h-2.5 ml-1" />
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
